import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type CSSProperties, type RefObject } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { ClapperMark } from '../components/Clapper';
import { CommentIcon, XIcon } from '../components/icons';
import { Spinner, cx } from '../components/ui';
import { track } from '../lib/amplitude';
import { ApiError } from '../lib/api';
import { shareUrl } from '../lib/env';
import type { Playback } from '../lib/types';
import { AudioPlayer } from '../player/AudioPlayer';
import { Comments } from '../player/Comments';
import { Player } from '../player/Player';
import { ReactionPill } from '../player/ReactionPill';
import { SlateClip } from '../player/SlateClip';
import { useReactions } from '../player/useReactions';
import { useViewBeacon } from '../player/useViewBeacon';
import { qk } from '../queries/keys';
import { fetchShare } from '../queries/share';

/**
 * /embed/:shareId. The player, for an iframe on somebody else's page.
 *
 * It fills whatever frame it is given and carries what a viewer needs
 * without leaving: the recording, the team's intro and outro around it, the
 * reactions, and comments in a drawer over the frame. A "View on ..." link
 * in the control bar is the way to the full page for everything else.
 *
 * Nobody signs in here. A sign-in popup from inside another site's frame is
 * unreliable, and a frame is where a visitor is least willing to make an
 * account, so reactions post under the browser's anonymous id and a comment
 * asks for a name, both of which the API has always accepted for a public
 * recording. Views count the same as on the share page.
 *
 * Access is the share page's. Whatever the recording asks of a viewer, an
 * account or an email or a plan, the frame shows the poster and sends them
 * to the full page instead of asking.
 */

/** How long the title bar stays after a touch, since a finger cannot hover. */
const TOUCH_CHROME_MS = 3000;

export function EmbedPage({ shareId }: { shareId: string }) {
  const { user, loading: authLoading } = useAuth();
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const query = useQuery({
    // Keyed on who is asking, like the share page: an owner previewing their
    // own embed sees their original upload while the ladder is cut.
    queryKey: [...qk.share(shareId), user?.uid ?? 'anon'],
    queryFn: () => fetchShare(shareId),
    enabled: !authLoading,
    refetchInterval: (result) => {
      const data = result.state.data;
      const busy = data?.status === 'processing' || data?.status === 'uploading';
      return busy || data?.rendering === true ? 5000 : false;
    },
    retry: (failureCount, error) => !(error instanceof ApiError && error.isNotFound) && failureCount < 2,
  });
  const video = query.data;
  const branding = video?.branding ?? null;
  const fullPage = shareUrl(shareId, branding?.domain);
  const siteName = branding?.name ?? 'Slate';

  // Same source rule as the share page, and the same reason to hold the first
  // raw URL: every poll signs a fresh one, and the player would restart.
  const heldRawUrl = useRef<string | null>(null);
  if (video?.rawPlaybackUrl && !heldRawUrl.current) heldRawUrl.current = video.rawPlaybackUrl;
  if (video?.playback) heldRawUrl.current = null;
  const rawPlaybackUrl = video?.playback ? null : heldRawUrl.current;
  const rawPosterUrl = video?.posterUrl ?? null;
  const playable: Playback | null = useMemo(
    () =>
      video?.playback ??
      (rawPlaybackUrl ? { hlsUrl: null, mp4Url: rawPlaybackUrl, posterUrl: rawPosterUrl, captionsUrl: null } : null),
    [video?.playback, rawPlaybackUrl, rawPosterUrl],
  );

  const isAudio = video?.kind === 'audio';
  const mediaRef: RefObject<HTMLMediaElement | null> = isAudio ? audioRef : videoRef;
  // Nothing plays here until the viewer has been let in on the full page.
  const walled = Boolean(video && (video.locked || video.gate === 'signIn' || video.gate === 'require'));
  const live = Boolean(video) && !walled;
  const beacon = useViewBeacon(shareId, live);
  const reactions = useReactions(shareId, live);
  const reactionsOn = live && video?.viewer?.reactionsEnabled !== false;
  const commentsOn = live && video?.viewer?.commentsEnabled !== false;

  /**
   * The team's slates around the recording, exactly as the share page plays
   * them: intro, recording, outro. 'intro' falls through to 'main' the moment
   * there is no intro, so an unbranded team takes the plain path.
   */
  const [phase, setPhase] = useState<'intro' | 'main' | 'outro'>('intro');
  const [ended, setEnded] = useState(false);
  const intro = branding?.intro ?? null;
  const outro = branding?.outro ?? null;
  const showIntro = phase === 'intro' && intro !== null;
  const showOutro = phase === 'outro' && outro !== null;

  // The comments drawer, and the composer inside it. Opened by the button in
  // the title bar and by the C key, as on the share page.
  const [drawer, setDrawer] = useState(false);
  const [composerFocus, setComposerFocus] = useState(0);
  const openComments = () => {
    track('Embed Comments Opened', { video_id: shareId });
    setDrawer(true);
    setComposerFocus((n) => n + 1);
  };
  useEffect(() => {
    if (!commentsOn) return;
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      if (event.key === 'c' && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        openComments();
      }
      if (event.key === 'Escape') setDrawer(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // openComments is stable enough: it only touches setters and the share id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentsOn, shareId]);

  const reported = useRef<string | null>(null);
  useEffect(() => {
    if (!video || reported.current === shareId) return;
    reported.current = shareId;
    track('Viewed Embed', { video_id: shareId, walled, referrer: document.referrer || null });
  }, [shareId, video, walled]);

  const played = useRef<string | null>(null);
  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;
    const onPlay = () => {
      if (played.current === shareId) return;
      played.current = shareId;
      track('Video Played', { video_id: shareId, surface: 'embed' });
    };
    media.addEventListener('play', onPlay);
    return () => media.removeEventListener('play', onPlay);
  }, [mediaRef, playable, shareId]);

  useEffect(() => {
    if (video?.title) document.title = `${video.title} · ${siteName}`;
    return () => {
      document.title = 'Slate';
    };
  }, [video?.title, siteName]);

  // The tab's icon follows the team's square logo, as on the share page.
  useEffect(() => {
    const square = branding?.squareLogoUrl;
    if (!square) return;
    const link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    if (!link) return;
    const original = link.href;
    link.href = square;
    return () => {
      link.href = original;
    };
  }, [branding?.squareLogoUrl]);

  // The frame may be any shape. The player is the recording's shape, and as
  // large as fits, so a wide frame does not push the controls off the bottom.
  const ratio = video?.width && video?.height && video.width > 0 ? video.height / video.width : 9 / 16;
  const fit: CSSProperties = { maxWidth: `calc(100dvh / ${ratio.toFixed(4)})` };

  // The title bar: on hover, and for a few seconds after a touch.
  const [chrome, setChrome] = useState(false);
  const chromeTimer = useRef<number | null>(null);
  const wakeChrome = () => {
    setChrome(true);
    if (chromeTimer.current) window.clearTimeout(chromeTimer.current);
    chromeTimer.current = window.setTimeout(() => setChrome(false), TOUCH_CHROME_MS);
  };

  const wallAction = `Watch on ${siteName}`;

  return (
    <div
      data-theme="dark"
      className="relative flex h-dvh w-full items-center justify-center overflow-hidden bg-black text-chalk"
      // The team's color reaches the play button and the scrub bar through
      // `brand-fill` and friends; see styles.css. Unset, they are chalk.
      style={
        branding?.brandColor
          ? ({ '--brand': branding.brandColor, '--brand-ink': branding.brandInk ?? '#0b0d10' } as CSSProperties)
          : undefined
      }
      onMouseEnter={() => setChrome(true)}
      onMouseLeave={() => setChrome(false)}
      onTouchStart={wakeChrome}
    >
      {query.isLoading ? <Spinner size={22} className="text-chalk-faint" /> : null}

      {query.isError ? (
        <Wall
          title="This recording is not available"
          detail="It may have been deleted, or it may be private and meant for somebody signed in."
          href={fullPage}
          action={`Open in ${siteName}`}
        />
      ) : null}

      {video ? (
        walled ? (
          <Wall
            title={video.title}
            posterUrl={video.posterUrl}
            detail={
              video.locked
                ? 'This recording is past the free plan and needs its owner.'
                : video.gate === 'signIn'
                  ? 'The owner asked that viewers sign in first.'
                  : 'The owner asked viewers for an email first.'
            }
            href={fullPage}
            action={wallAction}
          />
        ) : !playable ? (
          <Wall
            title={video.title}
            posterUrl={video.posterUrl}
            detail={video.status === 'failed' ? 'This recording failed to process.' : 'Still processing. It will play here as soon as it is ready.'}
            href={fullPage}
            action={`Open in ${siteName}`}
          />
        ) : isAudio ? (
          <div className="w-full max-w-2xl px-4">
            <AudioPlayer
              playback={playable}
              title={video.title}
              audioRef={audioRef}
              durationHint={video.durationSec}
              onProgress={beacon.report}
              onEnded={beacon.flush}
              reactions={reactions.reactions}
            />
            <a
              href={fullPage}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-block text-caption text-chalk-dim underline-offset-2 hover:text-chalk hover:underline"
            >
              View on {siteName} ↗
            </a>
          </div>
        ) : showIntro ? (
          <div className="w-full" style={fit}>
            <SlateClip slate={intro} role="intro" title={video.title} onDone={() => setPhase('main')} />
          </div>
        ) : showOutro ? (
          <div className="w-full" style={fit}>
            <SlateClip
              slate={outro}
              role="outro"
              onDone={() => {
                setPhase('main');
                setEnded(true);
              }}
            />
          </div>
        ) : (
          <div className="w-full" style={fit}>
            <Player
              playback={playable}
              title={video.title}
              videoRef={videoRef}
              // Straight on from the intro the viewer just watched.
              autoPlay={intro !== null && phase === 'main' && !ended}
              onProgress={(current, duration) => {
                beacon.report(current, duration);
                if (ended && current < duration - 0.5) setEnded(false);
              }}
              onEnded={() => {
                beacon.flush();
                if (outro) setPhase('outro');
                else setEnded(true);
              }}
              reactions={reactions.reactions}
              captionsDefault={video.viewer?.captionsOn}
              initialRate={video.viewer?.playbackRate}
              onCaptionsToggle={(on) => track('Captions Toggled', { video_id: shareId, on, surface: 'embed' })}
              onSpeedChange={(speed) => track('Playback Speed Changed', { video_id: shareId, speed, surface: 'embed' })}
              openIn={{ href: fullPage, label: `View on ${siteName}` }}
            />
          </div>
        )
      ) : null}

      {/* The title bar: whose recording, and the reactions and Comment.
          Shows while the pointer is over the frame, like the controls, and
          stays while the drawer is open. */}
      {video && live && playable ? (
        <div
          className={cx(
            'absolute left-0 right-0 top-0 flex items-center justify-between gap-3 px-3 py-2 transition-opacity duration-200',
            'bg-gradient-to-b from-black/75 to-transparent',
            chrome || drawer ? 'opacity-100' : 'pointer-events-none opacity-0 focus-within:pointer-events-auto focus-within:opacity-100',
          )}
        >
          <a
            href={fullPage}
            target="_blank"
            rel="noreferrer"
            className="flex min-w-0 items-center gap-2 text-caption text-chalk hover:underline"
            title={`View on ${siteName}`}
          >
            {branding?.squareLogoUrl ? (
              <img src={branding.squareLogoUrl} alt="" className="h-5 w-5 shrink-0 rounded-xs object-contain" />
            ) : branding?.hideSlateBranding ? null : (
              <ClapperMark size={16} />
            )}
            <span className="truncate font-medium">{video.title}</span>
          </a>
          <div className="flex shrink-0 items-center gap-2">
            {reactionsOn ? (
              <ReactionPill state={reactions} mediaRef={mediaRef} ready={Boolean(playable)} compact />
            ) : null}
            {commentsOn ? (
              <button
                type="button"
                onClick={openComments}
                className="flex h-9 items-center gap-1.5 rounded-full border border-hairline bg-slate-900/80 px-3 text-caption text-chalk hover:bg-slate-800"
              >
                <CommentIcon size={14} />
                Comment
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Comments, in a drawer over the frame. The composer asks a visitor
          for a name; a signed-in viewer (the owner previewing) is already
          somebody. */}
      {video && commentsOn && drawer ? (
        <aside
          aria-label="Comments"
          className="absolute inset-y-0 right-0 flex w-full flex-col border-l border-hairline bg-slate-950/95 backdrop-blur-sm sm:w-[360px]"
        >
          <div className="flex items-center justify-between border-b border-hairline px-4 py-3">
            <p className="text-body font-medium text-chalk">Comments</p>
            <button
              type="button"
              onClick={() => setDrawer(false)}
              aria-label="Close comments"
              className="rounded p-1 text-chalk-faint hover:text-chalk"
            >
              <XIcon size={16} />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
            <Comments
              shareId={shareId}
              mediaRef={mediaRef}
              playerReady={Boolean(playable)}
              authorName={video.author?.name}
              focusToken={composerFocus}
            />
          </div>
        </aside>
      ) : null}
    </div>
  );
}

/**
 * Where the frame cannot play: the poster, why, and the way to the page that
 * can. Opens in a new tab, since the frame's own page is somebody else's.
 */
function Wall({
  title,
  posterUrl,
  detail,
  href,
  action,
}: {
  title: string;
  posterUrl?: string | null;
  detail: string;
  href: string;
  action: string;
}) {
  return (
    <div className="relative flex h-full w-full items-center justify-center">
      {posterUrl ? (
        <img src={posterUrl} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30 blur-[2px]" />
      ) : null}
      <div className="relative max-w-sm px-6 text-center">
        <p className="text-h4 text-chalk">{title}</p>
        <p className="mt-1.5 text-caption text-chalk-dim">{detail}</p>
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className="brand-button mt-4 inline-flex h-9 items-center justify-center gap-2 rounded px-3.5 text-body font-medium"
        >
          {action}
        </a>
      </div>
    </div>
  );
}
